import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Image as ImageIcon, Plus, Trash2, Upload, Loader2, Bell, MessageCircle, Send, Instagram, Facebook } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_PLATFORMS = [
  { key: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, color: '#25D366', placeholder: '+39 339 1234567', hint: 'Numero con prefisso internazionale' },
  { key: 'telegram', label: 'Telegram', Icon: Send, color: '#0088cc', placeholder: 't.me/tuonegozio', hint: 'Canale o gruppo Telegram' },
  { key: 'instagram', label: 'Instagram', Icon: Instagram, color: '#E1306C', placeholder: '@tuonegozio', hint: 'Username senza URL' },
  { key: 'facebook', label: 'Facebook', Icon: Facebook, color: '#1877F2', placeholder: 'facebook.com/tuonegozio', hint: 'URL pagina Facebook' },
  { key: 'tiktok', label: 'TikTok', Icon: ImageIcon, color: '#000000', placeholder: '@tuonegozio', hint: 'Username TikTok' },
];

export default function HomeVetrinaManager({ getHeaders, shop, onShopRefresh }) {
  return (
    <div className="space-y-3">
      <BannerSlider getHeaders={getHeaders} banners={shop?.home_banners || []} onRefresh={onShopRefresh} />
      <SocialWidgetsManager getHeaders={getHeaders} currentWidgets={shop?.social_widgets || []} onRefresh={onShopRefresh} />
      <NewsletterToggle getHeaders={getHeaders} enabled={shop?.newsletter_enabled !== false} onRefresh={onShopRefresh} />
    </div>
  );
}

function BannerSlider({ getHeaders, banners, onRefresh }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [pendingFile, setPendingFile] = useState(null);

  const [imageUrls, setImageUrls] = useState({});

  React.useEffect(() => {
    // Load blob urls for existing banners
    banners.forEach(async b => {
      if (imageUrls[b.id] || !b.image_path) return;
      try {
        const res = await axios.get(`${API}/files/${b.image_path}`, { responseType: 'blob' });
        setImageUrls(prev => ({ ...prev, [b.id]: URL.createObjectURL(res.data) }));
      } catch { /* ignore */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banners]);

  const handleUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      if (title) fd.append('title', title);
      if (subtitle) fd.append('subtitle', subtitle);
      if (ctaLabel) fd.append('cta_label', ctaLabel);
      if (ctaUrl) fd.append('cta_url', ctaUrl);
      await axios.post(`${API}/shops/mine/banners`, fd, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Banner aggiunto');
      setPendingFile(null); setTitle(''); setSubtitle(''); setCtaLabel(''); setCtaUrl('');
      if (fileRef.current) fileRef.current.value = '';
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload banner');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo banner?')) return;
    try {
      await axios.delete(`${API}/shops/mine/banners/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Banner eliminato');
      onRefresh?.();
    } catch { toast.error('Errore eliminazione'); }
  };

  return (
    <div data-testid="banner-manager-card" className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-3">
      <div>
        <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider flex items-center gap-2">
          🎨 Banner Vetrina
        </h3>
        <p className="text-xs text-[#877E78] mt-1">
          Carosello 16:9 che appare in cima alla tua vetrina pubblica. Max 5 slide.
          <span className="block">Consiglio: immagini 1600×900 px (o multipli).</span>
        </p>
      </div>

      {/* Existing banners */}
      {banners.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {banners.map(b => (
            <div key={b.id} className="relative rounded-lg overflow-hidden border border-[#E8E3D9] bg-[#F0EBE1] aspect-video">
              {imageUrls[b.id] ? (
                <img src={imageUrls[b.id]} alt={b.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Loader2 size={18} className="animate-spin text-[#877E78]" /></div>
              )}
              <button
                data-testid={`delete-banner-${b.id}`}
                onClick={() => handleDelete(b.id)}
                className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-500"
              >
                <Trash2 size={13} />
              </button>
              {(b.title || b.subtitle) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  {b.title && <p className="text-white text-xs font-bold truncate">{b.title}</p>}
                  {b.subtitle && <p className="text-white/80 text-[10px] truncate">{b.subtitle}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new banner */}
      {banners.length < 5 ? (
        <div className="border-2 border-dashed border-[#C25934]/30 rounded-xl p-3 space-y-2">
          {!pendingFile ? (
            <label className="flex flex-col items-center gap-2 cursor-pointer py-4">
              <Upload size={22} className="text-[#C25934]" />
              <span className="text-sm font-medium text-[#2A2624]">Aggiungi banner</span>
              <span className="text-[10px] text-[#877E78]">JPG / PNG · max 3 MB</span>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="banner-file-input"
                onChange={e => e.target.files?.[0] && setPendingFile(e.target.files[0])}
              />
            </label>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-[#5C554F]">
                <ImageIcon size={14} /> {pendingFile.name} · {Math.round(pendingFile.size/1024)}KB
                <button onClick={() => setPendingFile(null)} className="ml-auto text-red-500"><Trash2 size={13} /></button>
              </div>
              <Input data-testid="banner-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Titolo banner (opzionale)" className="h-10 rounded-lg border-[#E8E3D9] text-sm" />
              <Input data-testid="banner-subtitle" value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Sottotitolo (opzionale)" className="h-10 rounded-lg border-[#E8E3D9] text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="banner-cta-label" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} placeholder="Testo bottone" className="h-10 rounded-lg border-[#E8E3D9] text-sm" />
                <Input data-testid="banner-cta-url" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="Link (URL)" className="h-10 rounded-lg border-[#E8E3D9] text-sm" />
              </div>
              <Button
                data-testid="banner-upload-btn"
                onClick={handleUpload}
                disabled={uploading}
                className="w-full h-10 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-sm"
              >
                {uploading ? <><Loader2 size={14} className="mr-1 animate-spin" /> Caricamento...</> : <><Plus size={14} className="mr-1" /> Pubblica banner</>}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-[#877E78] text-center py-3">Hai raggiunto il massimo (5 banner). Rimuovine uno per aggiungerne altri.</p>
      )}
    </div>
  );
}

function SocialWidgetsManager({ getHeaders, currentWidgets, onRefresh }) {
  const [widgets, setWidgets] = useState(() => {
    const byKey = Object.fromEntries((currentWidgets || []).map(w => [w.platform, w]));
    return SOCIAL_PLATFORMS.map(p => ({
      platform: p.key,
      visible: byKey[p.key]?.visible ?? false,
      handle: byKey[p.key]?.handle ?? '',
      cta_text: byKey[p.key]?.cta_text ?? '',
    }));
  });
  const [saving, setSaving] = useState(false);

  const update = (key, field, value) => {
    setWidgets(ws => ws.map(w => w.platform === key ? { ...w, [field]: value } : w));
  };

  const save = async () => {
    setSaving(true);
    try {
      const visible = widgets.filter(w => w.visible && w.handle);
      await axios.put(`${API}/shops/mine`, { social_widgets: visible }, { withCredentials: true, headers: getHeaders() });
      toast.success('Widget social salvati');
      onRefresh?.();
    } catch { toast.error('Errore salvataggio social'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="social-widgets-card" className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-3">
      <div>
        <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider">📲 Widget Social</h3>
        <p className="text-xs text-[#877E78] mt-1">
          Canali promossi in fondo alla tua vetrina pubblica. Attiva solo quelli che usi davvero.
        </p>
      </div>
      {SOCIAL_PLATFORMS.map(p => {
        const { Icon } = p;
        const w = widgets.find(x => x.platform === p.key);
        return (
          <div key={p.key} className="border border-[#E8E3D9] rounded-lg p-2.5 space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: p.color }}>
                <Icon size={16} />
              </div>
              <span className="font-semibold text-sm text-[#2A2624] flex-1">{p.label}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`social-toggle-${p.key}`}
                  checked={w.visible}
                  onChange={e => update(p.key, 'visible', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-[#E8E3D9] rounded-full peer-checked:bg-[#C25934] after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
              </label>
            </div>
            {w.visible && (
              <>
                <Input
                  data-testid={`social-handle-${p.key}`}
                  value={w.handle}
                  onChange={e => update(p.key, 'handle', e.target.value)}
                  placeholder={p.placeholder}
                  className="h-9 rounded-lg border-[#E8E3D9] text-xs"
                />
                <p className="text-[10px] text-[#877E78]">{p.hint}</p>
              </>
            )}
          </div>
        );
      })}
      <Button
        data-testid="save-social-widgets-btn"
        onClick={save}
        disabled={saving}
        className="w-full h-10 rounded-full bg-[#4A6B53] hover:bg-[#3a5643] text-white text-sm"
      >
        {saving ? 'Salvataggio...' : '💾 Salva widget social'}
      </Button>
    </div>
  );
}

function NewsletterToggle({ getHeaders, enabled, onRefresh }) {
  const [value, setValue] = useState(enabled);
  const [saving, setSaving] = useState(false);
  const [subs, setSubs] = useState(null);

  React.useEffect(() => {
    axios.get(`${API}/shops/mine/newsletter-subs`, { withCredentials: true, headers: getHeaders() })
      .then(r => setSubs(r.data?.total || 0))
      .catch(() => setSubs(0));
    // eslint-disable-next-line
  }, []);

  const toggle = async () => {
    setSaving(true);
    const newVal = !value;
    try {
      await axios.put(`${API}/shops/mine`, { newsletter_enabled: newVal }, { withCredentials: true, headers: getHeaders() });
      setValue(newVal);
      toast.success(newVal ? 'Newsletter attivata' : 'Newsletter disattivata');
      onRefresh?.();
    } catch { toast.error('Errore'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="newsletter-toggle-card" className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-2">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#C25934]/10 flex items-center justify-center text-[#C25934] flex-shrink-0">
          <Bell size={18} />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-[#2A2624]">Newsletter Clienti</h3>
          <p className="text-xs text-[#877E78] mt-0.5">
            I clienti possono iscriversi per ricevere avvisi sui nuovi volantini.
          </p>
          {subs !== null && (
            <p className="text-[11px] text-[#4A6B53] font-medium mt-1">
              👥 {subs} iscritti attualmente
            </p>
          )}
        </div>
        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            data-testid="newsletter-toggle"
            checked={value}
            onChange={toggle}
            disabled={saving}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-[#E8E3D9] rounded-full peer-checked:bg-[#C25934] after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
        </label>
      </div>
    </div>
  );
}
